import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { useForm } from "react-hook-form";
import {usePage} from "@inertiajs/react";
import {zodResolver} from "@hookform/resolvers/zod";
import {NurseCreateSchema, NurseFormPayload} from "@/lib/types/forms.ts";
import {useEffect} from "react";

interface CreateNurseDialogProps {
    isOpen: boolean;
    onOpenChange: (open: boolean) => void;
    onCreate: (data: NurseFormPayload) => Promise<void>;
}

export default function CreateNurseDialog({
                                              isOpen,
                                              onOpenChange,
                                              onCreate,
                                          }
                                          : CreateNurseDialogProps) {

    const {translations} = usePage().props as unknown as { translations: any };
    const t = translations.create_dialog;

    const DEFAULT_VALUES = {
        name: "",
        username: "",
        phone_number: "",
        password: "",
        password_confirmation: "",
    };

    const form = useForm<NurseFormPayload>({
        resolver: zodResolver(NurseCreateSchema),
        defaultValues: DEFAULT_VALUES,
    });

    useEffect(() => {
        if (isOpen) form.reset(DEFAULT_VALUES);
    }, [isOpen, form]);

    const handleSubmit = async (data: NurseFormPayload) => {
        await onCreate(data);
    };

    return (
        <Dialog open={isOpen} onOpenChange={onOpenChange}>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>{t.title}</DialogTitle>
                </DialogHeader>

                <Form {...form}>
                    <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
                        <FormField
                            control={form.control}
                            name="name"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>{t.labels.name}</FormLabel>
                                    <FormControl>
                                        <Input {...field} placeholder={t.placeholders.name}/>
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />

                        <FormField
                            control={form.control}
                            name="username"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>{t.labels.username}</FormLabel>
                                    <FormControl>
                                        <Input {...field} placeholder={t.placeholders.username}/>
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />

                        <FormField
                            control={form.control}
                            name="phone_number"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>{t.labels.phone_number}</FormLabel>
                                    <FormControl>
                                        <Input {...field} placeholder={t.placeholders.phone_number} />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />

                        <FormField
                            control={form.control}
                            name="password"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>{t.labels.password}</FormLabel>
                                    <FormControl>
                                        <Input {...field} placeholder={t.placeholders.password} type="password" />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />

                        <FormField
                            control={form.control}
                            name="password_confirmation"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>{t.labels.password_confirmation}</FormLabel>
                                    <FormControl>
                                        <Input {...field} placeholder={t.placeholders.password_confirmation} type="password" />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />

                        <DialogFooter className="flex justify-end gap-2">
                            <Button variant="outline" type="button" onClick={() => onOpenChange(false)}>
                                {t.buttons.cancel}
                            </Button>
                            <Button type="submit">
                                {t.buttons.create}
                            </Button>
                        </DialogFooter>

                    </form>
                </Form>
            </DialogContent>
        </Dialog>
    );
}